import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject } from 'rxjs'
import { NestedTreeControl } from '@angular/cdk/tree'
import {
  MatNestedTreeNode,
  MatTree,
  MatTreeNestedDataSource,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodeOutlet,
  MatTreeNodeToggle,
} from '@angular/material/tree'
import { BomItemDetailDialogComponent } from '../bom-item-detail-dialog/bom-item-detail-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { Bom, BomComponent, BomItemType, NamedBomComponent } from '../bom'
import { MaterialComponent } from '../material-component'
import { ElderQuantityPipe } from '@elderbyte/ngx-starter'
import { NgTemplateOutlet } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'

@Component({
  selector: 'app-bom-editor',
  templateUrl: './bom-editor.component.html',
  styleUrls: ['./bom-editor.component.scss'],
  imports: [
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodeToggle,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatNestedTreeNode,
    MatTreeNodeOutlet,
    ElderQuantityPipe,
  ],
})
export class BomEditorComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  public readonly MaterialComponent = MaterialComponent

  public readonly bom$ = new BehaviorSubject<Bom>(null)
  @Output()
  public readonly bomChanged = new EventEmitter<Bom>()
  public readonly treeControl = new NestedTreeControl<NamedBomComponent>(
    (node) => node.component.namedItems
  )
  public readonly dataSource = new MatTreeNestedDataSource<NamedBomComponent>()
  private matDialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set bom(bom: Bom) {
    this.bom$.next(bom)
    this.dataSource.data = bom.namedItems
  }

  @Input()
  public set namedComponent(item: NamedBomComponent) {
    this.bom = Bom.of(item)
  }

  @Input()
  public set component(item: BomComponent) {
    this.namedComponent = NamedBomComponent.of('', item)
  }

  /***************************************************************************
   *                                                                         *
   * Public Api                                                              *
   *                                                                         *
   **************************************************************************/

  public openDetail(item: NamedBomComponent): void {
    const dialogRef = this.matDialog.open(BomItemDetailDialogComponent, {
      data: item,
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Save if allowed
      }
    })
  }

  public getIcon(component: BomComponent): string {
    if (!component) {
      return null
    }

    switch (component.type) {
      case BomItemType.Material:
        return 'layers'
      case BomItemType.Config:
        return 'code'
      case BomItemType.Attachment:
        return 'attach_file'
      case BomItemType.NonStock:
        return 'fiber_manual_record'
      case BomItemType.Phantom:
        return 'tonality'
      case BomItemType.Text:
        return 'title'
      case BomItemType.PropertySet:
        return 'list_alt'

      default:
        return 'help'
    }
  }

  public hasChild = (_: number, node: NamedBomComponent) =>
    !!node.component.namedItems && node.component.namedItems.length > 0
}
